import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, Users, Quote, Sparkles } from 'lucide-react';
import SEO from '@/components/SEO';
import ZazuLogo from '@/components/ZazuLogo';
import SocialFooterLinks from '@/components/SocialFooterLinks';

/**
 * Customers page — audit D2.4, D3.5.
 * Currently a stub inviting first families to share stories.
 * Real, named, photographed testimonials will replace the invite as they come in.
 */
const CustomersPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-amber-50/40">
      <SEO
        title="Zazu Customer Stories — Homeschool Families and Sunday Schools"
        description="Real outcome stories from homeschool families and Sunday school teachers using Zazu. Named testimonials with photos, coming soon."
        path="/customers"
      />

      <header className="p-4 border-b border-slate-100 bg-white/70 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2 text-emerald-600 hover:text-emerald-700">
            <ArrowLeft className="w-5 h-5" />
            <ZazuLogo size="sm" />
          </Link>
          <div className="flex gap-3">
            <Link to="/pricing"><Button variant="ghost" size="sm">Pricing</Button></Link>
            <Link to="/register"><Button size="sm" className="bg-teal-700 hover:bg-teal-800">Start free</Button></Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-amber-100 border border-amber-200 rounded-full px-4 py-1.5 mb-5">
            <Users className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-semibold text-amber-700">Customer stories</span>
          </div>
          <h1 className="font-fredoka text-4xl sm:text-5xl font-bold text-slate-900 mb-4" data-testid="customers-h1">
            Stories from our first families
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            We're at the start of Zazu. Real testimonials — with photos, full names, and specific outcomes like "went from letter sounds to CVC words in six weeks" — will appear here as they come in.
          </p>
        </div>

        <Card className="mb-8 border-2 border-dashed border-emerald-200 bg-white" data-testid="customers-invite-card">
          <CardContent className="p-8 text-center">
            <Quote className="w-10 h-10 text-emerald-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-900 mb-3">Using Zazu with your kids?</h2>
            <p className="text-slate-600 mb-6 max-w-lg mx-auto leading-relaxed">
              We'd love to feature your family. Share what your child has learned, and we'll credit you by name with a real photo. No AI, no gradient circles, no invented quotes.
            </p>
            <Link to="/help">
              <Button size="lg" className="bg-teal-700 hover:bg-teal-800 rounded-full" data-testid="customers-share-btn">
                Share your story
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Placeholder cards — will be replaced with real testimonials */}
        <div className="grid sm:grid-cols-2 gap-4 mb-12">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="border-slate-200 bg-slate-50/50" data-testid={`customers-placeholder-${i}`}>
              <CardContent className="p-5">
                <div className="w-full h-24 rounded-lg bg-slate-100 flex items-center justify-center mb-3">
                  <span className="text-xs text-slate-400 uppercase tracking-wide">Photo + quote coming soon</span>
                </div>
                <div className="h-3 bg-slate-100 rounded mb-2 w-3/4" />
                <div className="h-3 bg-slate-100 rounded w-1/2" />
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="text-center bg-white rounded-2xl border-2 border-emerald-100 p-8">
          <div className="inline-flex items-center gap-2 mb-3">
            <Sparkles className="w-5 h-5 text-emerald-600" />
            <span className="text-sm font-semibold text-emerald-700">Free forever plan</span>
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Try Zazu with your family</h2>
          <p className="text-slate-600 mb-6">30-day money-back guarantee on any paid plan.</p>
          <Link to="/register">
            <Button size="lg" className="bg-teal-700 hover:bg-teal-800 h-14 px-8 rounded-full" data-testid="customers-cta-btn">
              Start free
            </Button>
          </Link>
        </div>
      </main>

      <footer className="bg-slate-900 text-slate-400 py-10 px-4 mt-16">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex justify-center mb-4">
            <ZazuLogo size="md" className="[&_span]:text-white" />
          </div>
          <p className="text-sm mb-3">Impacting lives, one syllable at a time.</p>
          <SocialFooterLinks />
          <p className="text-xs mt-4">&copy; {new Date().getFullYear()} Zazu. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default CustomersPage;
